import {BoltOutlined} from '@mui/icons-material';
import {Box, Tab, Tabs, TabsProps, Typography} from '@mui/material';
import {FC, useMemo, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {tagsApi} from 'src/api';
import {useAppSelector, useDefineIsRealEstateRole} from 'src/app/hooks';
import {ColoredStatus} from 'src/components/common/ColoredStatus';
import {tabsBandProps, tabsBandSx} from 'src/components/common/relationshipsPanel.sx';
import {useCan, usePersona} from 'src/permissions/hooks';
import {CONNECTOR_STATUS_TYPE} from 'src/types';
import {
    badgeSx,
    connectorBadgeSx,
    connectorLabelSx,
    connectorTabEvenSx,
    connectorTabSx,
    countSx,
    headerSx,
    tabHeadSx,
    tabLabelSx,
    titleSx,
    wrapperSx,
} from './ActiveCharging.sx';
import {isSessionRunning, liveConnectorStatus} from './ActiveCharging.state';
import {ActiveChargingType} from './ActiveCharging.types';
import {ConnectorSession} from './ConnectorSession';

/** Where an equal share stops being readable and scrolling is the lesser evil. */
const EVEN_TAB_LIMIT = 4;

/**
 * The charger, and one tab per socket.
 *
 * It used to be one panel for the whole station: a single status, a single set of figures, a single
 * card and a single button, each of them quietly belonging to `connectors[0]`. A station charging two
 * cars showed one reading, taken from whichever update had arrived last, and left the other session
 * off the screen entirely.
 *
 * The band is the one the relationships panels use, so switching sockets is the gesture this
 * dashboard already has. A tab carries its connector's own status, which is what a band costs and
 * has to repay: only one socket is open at a time, so the others have to say what they are doing
 * from the tab itself - otherwise a fault on the socket nobody selected is invisible.
 */
export const ActiveCharging: FC<ActiveChargingType> = ({data}) => {
    const persona = usePersona();
    const authInfo = useAppSelector((state) => state.common.authInfo);
    const CONNECTOR_STATUS: CONNECTOR_STATUS_TYPE[] = useAppSelector(
        (state) => state.common.CONNECTOR_STATUS
    );
    const isRealEstate = useDefineIsRealEstateRole();

    // GET /transactions/active is transactions.readActive; without it the status and the connectors
    // still come from the charger itself, and asking would only be refused.
    const canReadActive = useCan('transactions.readActive');
    const canManageCharging = useCan('chargers.startStop');

    // The sticker on the socket, the QR code and the start request all name a connector by its own
    // number, and the server returns connectors in an unordered `Set` it sorts nowhere - so the
    // order they are read in is decided here rather than inherited.
    const connectors = useMemo(
        () => [...(data.connectors ?? [])].sort((a, b) => a.connectorId - b.connectorId),
        [data.connectors]
    );

    const showSpec = !persona.isDriver && !persona.isEmployee && !isRealEstate;

    // Up to four, the tabs divide the band equally - two take half each, three a third. Past that a
    // share of a narrow rail is a sliver with two lines of text in it, so the band scrolls instead.
    const evenTabs = connectors.length <= EVEN_TAB_LIMIT;

    // Keyed by the socket number, because that is what a description carries - reading them off by
    // position described the other socket, the `Set` the server returns having no order to inherit.
    const specs = useMemo(
        () => new Map(data.model.connectorDescriptions.map((item) => [item.connectorId, item])),
        [data.model.connectorDescriptions]
    );

    const statuses = useMemo(
        () => new Map(
            connectors.map(
                (connector) => [connector.id, liveConnectorStatus(CONNECTOR_STATUS, data.id, connector)]
            )
        ),
        [connectors, CONNECTOR_STATUS, data.id]
    );

    // Counted over the connectors rather than over the map's values: this project's TypeScript
    // target cannot spread a Map iterator.
    const inUse = useMemo(
        () => connectors.filter(
            (connector) => isSessionRunning(statuses.get(connector.id) ?? connector.currentStatus)
        ).length,
        [connectors, statuses]
    );

    // Opened on a socket that is doing something: with one car charging and one bay free, the running
    // session is what somebody came to the page for. Decided once, from the charger as it was read,
    // and never again - a tab that moved by itself when a session started elsewhere would take the
    // page away from whoever was reading it.
    const [selectedId, setSelectedId] = useState<number | undefined>(
        () => (connectors.find((connector) => isSessionRunning(connector.currentStatus))
            ?? connectors[0])?.id
    );

    const handleSelect: TabsProps['onChange'] = (_event, value) => setSelectedId(value);

    // A selection can outlive what it pointed at, when the page is reused for another charger.
    const selected = connectors.find((connector) => connector.id === selectedId) ?? connectors[0];

    // The first card the picker would offer, asked for once for the whole card: most people charge
    // with the one card they hold, and had to open a list to pick it every time. The same request the
    // picker makes, active cards only, so the default is always the picker's first row.
    const tagCompanyId = authInfo?.employee?.company?.id || authInfo?.company?.id;
    const {data: firstTagPage} = tagsApi.useGetTagsQuery({
        params: {page: 0, size: 1},
        data: {businessId: data.business.id, companyId: tagCompanyId, blocked: false},
    }, {skip: !canManageCharging});

    if (!selected) {
        return null;
    }

    return (
        <Box sx={wrapperSx}>
            <Box sx={headerSx}>
                <Box sx={badgeSx}>
                    <BoltOutlined/>
                </Box>
                <Typography sx={titleSx}>
                    <FormattedMessage id="charging"/>
                </Typography>
                <Typography sx={countSx}>
                    <FormattedMessage
                        id="connectorsInUse"
                        values={{inUse, total: connectors.length}}
                    />
                </Typography>
            </Box>

            {/* One socket is no choice, and a band of one tab is a control that does nothing. */}
            {connectors.length > 1 && (
                <Box sx={tabsBandSx}>
                    <Tabs
                        {...(evenTabs ? {variant: 'fullWidth' as const} : tabsBandProps)}
                        value={selected.id}
                        onChange={handleSelect}
                        aria-label={`charger ${data.chargePointId} connectors`}
                    >
                        {connectors.map((connector) => {
                            const status = statuses.get(connector.id) ?? connector.currentStatus;
                            const running = isSessionRunning(status);

                            return (
                                <Tab
                                    key={connector.id}
                                    value={connector.id}
                                    sx={evenTabs ? connectorTabEvenSx : connectorTabSx}
                                    label={(
                                        <Box sx={tabLabelSx}>
                                            <Box sx={tabHeadSx}>
                                                <Box sx={connectorBadgeSx(running)}>
                                                    {connector.connectorId}
                                                </Box>
                                            {/* The type when the reader is entitled to it, and the
                                                word "Connector" when they are not - a bare digit
                                                beside nothing does not say what it counts. */}
                                                {showSpec && specs.get(connector.connectorId) ? (
                                                    <Box component="span" sx={connectorLabelSx}>
                                                        {specs.get(connector.connectorId)?.connectorType}
                                                    </Box>
                                                ) : (
                                                    <Box component="span" sx={connectorLabelSx}>
                                                        <FormattedMessage
                                                            id="connectorNumber"
                                                            values={{number: connector.connectorId}}
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                            <ColoredStatus label={status} fontSize={11} component="span"/>
                                        </Box>
                                    )}
                                />
                            );
                        })}
                    </Tabs>
                </Box>
            )}

            <ConnectorSession
                key={selected.id}
                charger={data}
                connector={selected}
                status={statuses.get(selected.id) ?? selected.currentStatus}
                description={specs.get(selected.connectorId)}
                showSpec={showSpec}
                standalone={connectors.length === 1}
                canManage={canManageCharging}
                canReadActive={canReadActive}
                defaultTag={firstTagPage?.content?.[0]?.idTag}
            />
        </Box>
    );
};
